/**
 * HoldCard — one already-placed hold, rendered as its own card.
 *
 * Reads only what the API sends (see holdUtils.buildHoldCards for the view
 * model and the legacy-row fallbacks) and writes nothing itself: changing the
 * hold type and releasing the date are both handed back up to the page, which
 * still calls the same PATCH and DELETE it always has. Changing the type in
 * place is what keeps a hold's original hold_created_at — and therefore its
 * ELAPSED count — rather than releasing and re-placing it.
 */
import React, { useEffect, useRef, useState } from 'react'
import {
    BORDER_LIGHT,
    CHIP_BG,
    FONT,
    FONT_DISPLAY,
    GREEN,
    GREEN_HOVER,
    HAIRLINE,
    INK,
    INK_SOFT,
    MUTED,
    RED,
    RED_BORDER,
    RED_FILL,
    TAN_BORDER,
    TAN_FILL,
    TAN_INK,
    cardStyle,
    sectionLabelStyle,
} from './holdTheme'
import {
    HOLD_TYPE_OPTIONS,
    formatCardDate,
    formatFullDate,
    holdTypeLabel,
} from './holdUtils'

/* The hold-type chooser, as the design draws it: a solid dark-green button
 * showing the current type with a caret. Same three options and same handler
 * as the select it replaces. */
function HoldTypeMenu({ value, onChange, disabled }) {
    const [open, setOpen] = useState(false)
    const ref = useRef(null)

    useEffect(() => {
        if (!open) return undefined
        const onDoc = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false)
        }
        // Capture phase + preventDefault: the modal closes on Escape only when
        // the event has not already been handled, so this shuts the menu
        // without taking the whole dialog down with it.
        const onKey = (e) => {
            if (e.key === 'Escape') {
                e.preventDefault()
                setOpen(false)
            }
        }
        document.addEventListener('mousedown', onDoc)
        document.addEventListener('keydown', onKey, true)
        return () => {
            document.removeEventListener('mousedown', onDoc)
            document.removeEventListener('keydown', onKey, true)
        }
    }, [open])

    return (
        <div ref={ref} style={{ position: 'relative', flex: 'none' }}>
            <button
                type="button"
                disabled={disabled}
                aria-haspopup="listbox"
                aria-expanded={open}
                onClick={() => setOpen((o) => !o)}
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 7,
                    height: 32,
                    padding: '0 12px',
                    borderRadius: 8,
                    border: 'none',
                    background: GREEN,
                    color: '#fff',
                    fontFamily: FONT,
                    fontSize: 12.5,
                    fontWeight: 600,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    opacity: disabled ? 0.6 : 1,
                    transition: 'background .12s',
                }}
                onMouseEnter={(e) => {
                    if (!disabled) e.currentTarget.style.background = GREEN_HOVER
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.background = GREEN
                }}
            >
                {holdTypeLabel(value)}
                <svg
                    width="11"
                    height="11"
                    viewBox="0 0 14 14"
                    fill="none"
                    style={{
                        transform: open ? 'rotate(180deg)' : 'none',
                        transition: 'transform .15s',
                        flexShrink: 0,
                    }}
                >
                    <path
                        d="M3.5 5.25L7 8.75l3.5-3.5"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </button>

            {open && (
                /* Opens upward. These cards are the last thing in a scrolling
                 * modal body, so a menu dropped below the button would be
                 * clipped by the scroll container. */
                <div
                    role="listbox"
                    style={{
                        position: 'absolute',
                        bottom: 'calc(100% + 6px)',
                        left: 0,
                        minWidth: 190,
                        background: '#fff',
                        border: `1px solid ${BORDER_LIGHT}`,
                        borderRadius: 10,
                        boxShadow: '0 12px 32px rgba(16,24,40,0.16)',
                        padding: 4,
                        zIndex: 70,
                    }}
                >
                    {HOLD_TYPE_OPTIONS.map((o) => {
                        const active = o.value === value
                        return (
                            <button
                                key={o.value}
                                type="button"
                                role="option"
                                aria-selected={active}
                                onClick={() => {
                                    setOpen(false)
                                    if (!active) onChange(o.value)
                                }}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 8,
                                    width: '100%',
                                    textAlign: 'left',
                                    padding: '8px 10px',
                                    borderRadius: 7,
                                    border: 'none',
                                    background: 'transparent',
                                    cursor: 'pointer',
                                    fontFamily: FONT,
                                    fontSize: 13,
                                    fontWeight: active ? 600 : 500,
                                    color: INK,
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.background = '#F3F3F0'
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.background =
                                        'transparent'
                                }}
                            >
                                {o.label}
                                {active && (
                                    <svg
                                        width="13"
                                        height="13"
                                        viewBox="0 0 14 14"
                                        fill="none"
                                    >
                                        <path
                                            d="M2.5 7.5l3 3 6-6.5"
                                            stroke={GREEN}
                                            strokeWidth="1.8"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                )}
                            </button>
                        )
                    })}
                </div>
            )}
        </div>
    )
}

/* REQUESTED / ELAPSED. The number is set heavier than the unit; a free-text
 * timeframe that is not a number-and-word ("till they confirm funding") has no
 * number to weight, so it renders whole at the unit's size. */
function Stat({ label, parts, tone }) {
    return (
        <div style={{ flex: '1 1 0', minWidth: 0 }}>
            <span style={sectionLabelStyle}>{label}</span>
            <div
                style={{
                    marginTop: 5,
                    fontFamily: FONT,
                    color: tone,
                    lineHeight: 1.15,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }}
                title={parts ? `${parts.value} ${parts.unit}`.trim() : undefined}
            >
                {!parts ? (
                    <span style={{ fontSize: 20, fontWeight: 600, color: MUTED }}>
                        —
                    </span>
                ) : parts.value ? (
                    <>
                        <span style={{ fontSize: 20, fontWeight: 700 }}>
                            {parts.value}
                        </span>{' '}
                        <span style={{ fontSize: 14, fontWeight: 500 }}>
                            {parts.unit}
                        </span>
                    </>
                ) : (
                    <span style={{ fontSize: 14, fontWeight: 600 }}>
                        {parts.unit}
                    </span>
                )}
            </div>
        </div>
    )
}

export default function HoldCard({
    card,
    onChangeType,
    onRelease,
    releasing = false,
    releaseDisabled = false,
}) {
    const { isOverdue } = card

    return (
        <div style={{ ...cardStyle, padding: 16 }}>
            {/* Date, portion, and where the hold stands. */}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    marginBottom: 14,
                }}
            >
                <span
                    title={formatFullDate(card.date)}
                    style={{
                        fontFamily: FONT_DISPLAY,
                        fontSize: 17,
                        fontWeight: 700,
                        color: INK,
                        whiteSpace: 'nowrap',
                    }}
                >
                    {formatCardDate(card.date)}
                </span>
                <span
                    style={{
                        flex: 'none',
                        fontFamily: FONT,
                        fontSize: 11,
                        fontWeight: 600,
                        color: INK_SOFT,
                        background: CHIP_BG,
                        border: `1px solid ${HAIRLINE}`,
                        borderRadius: 6,
                        padding: '2px 7px',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {card.portionLabel}
                </span>
                <span style={{ flex: 1 }} />
                <span
                    style={{
                        flex: 'none',
                        fontFamily: FONT,
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        borderRadius: 999,
                        padding: '4px 10px',
                        whiteSpace: 'nowrap',
                        background: isOverdue ? RED_FILL : TAN_FILL,
                        border: `1px solid ${isOverdue ? RED_BORDER : TAN_BORDER}`,
                        color: isOverdue ? RED : TAN_INK,
                    }}
                >
                    {card.badgeLabel}
                </span>
            </div>

            {/* What was asked for, against what has actually gone by. */}
            <div style={{ display: 'flex', alignItems: 'stretch', gap: 16 }}>
                <Stat
                    label="Requested"
                    parts={card.requestedParts}
                    tone={INK}
                />
                <div
                    aria-hidden="true"
                    style={{ width: 1, background: HAIRLINE, flex: 'none' }}
                />
                <Stat
                    label="Elapsed"
                    parts={card.elapsedParts}
                    tone={isOverdue ? RED : GREEN}
                />
            </div>

            {/* Only ever rendered around timestamps that exist — sending is
                disabled, so this line is absent on every hold today. */}
            {card.statusSentence && (
                <p
                    style={{
                        margin: '12px 0 0',
                        fontFamily: FONT,
                        fontSize: 12,
                        lineHeight: 1.45,
                        color: isOverdue ? RED : MUTED,
                    }}
                >
                    {card.statusSentence}
                </p>
            )}

            <div
                style={{
                    height: 1,
                    background: HAIRLINE,
                    margin: '14px 0 12px',
                }}
            />

            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                }}
            >
                <HoldTypeMenu
                    value={card.holdType}
                    onChange={onChangeType}
                    disabled={releasing}
                />
                <button
                    type="button"
                    onClick={onRelease}
                    disabled={releaseDisabled}
                    style={{
                        border: 'none',
                        background: 'transparent',
                        padding: '4px 2px',
                        fontFamily: FONT,
                        fontSize: 12.5,
                        fontWeight: 600,
                        color: RED,
                        cursor: releaseDisabled ? 'wait' : 'pointer',
                        flex: 'none',
                    }}
                >
                    {releasing ? 'Releasing…' : 'Release'}
                </button>
            </div>
        </div>
    )
}
